import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { TERMS_DOC, PRIVACY_DOC } from "./legal-content";

/** Renders a legal document (terms / privacy) in GenQuotes brand style:
 *  navy hero header with page title + last-updated, white content card with
 *  readable body copy and numbered section headings. */
function LegalDoc({ doc }) {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="bg-[#0A1628] text-[#F8FAFC]">
      {/* Navy hero header */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 so-grid opacity-25 pointer-events-none" />
        <div className="absolute -top-40 left-1/2 -translate-x-1/2 w-[600px] h-[600px] so-glow-blue pointer-events-none" />
        <div className="relative max-w-3xl mx-auto px-6 pt-16 sm:pt-24 pb-12 sm:pb-16 text-center">
          <div className="uppercase text-xs tracking-widest text-[#06B6D4] font-mono">
            Legal
          </div>
          <h1 className="mt-3 font-display text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight">
            {doc.title}
          </h1>
          <p className="mt-4 text-[#94A3B8] text-sm font-mono">
            Last updated: {doc.lastUpdated}
          </p>
        </div>
      </section>

      {/* White readable content area */}
      <section className="pb-20">
        <div className="max-w-3xl mx-auto px-4 sm:px-6">
          <article
            data-testid={`legal-doc-${doc.slug}`}
            className="bg-white text-slate-800 rounded-2xl shadow-2xl p-6 sm:p-10 lg:p-12 ring-1 ring-slate-900/5"
            style={{ fontFamily: "'IBM Plex Sans', system-ui, sans-serif" }}
          >
            {doc.intro && (
              <p className="text-[15px] sm:text-base leading-relaxed text-slate-700 mb-8">
                {doc.intro}
              </p>
            )}

            {doc.sections.map((sec, i) => (
              <section key={i} className="mb-8 last:mb-0">
                <h2 className="font-display text-xl sm:text-2xl font-bold tracking-tight text-slate-900 mb-3">
                  {sec.heading}
                </h2>
                {sec.lead && (
                  <p className="text-[15px] leading-relaxed text-slate-700 mb-4">
                    {sec.lead}
                  </p>
                )}
                {sec.bullets && sec.bullets.length > 0 && (
                  <ul className="list-disc pl-5 space-y-2 text-[15px] leading-relaxed text-slate-700 mb-4">
                    {sec.bullets.map((b, bi) => (
                      <li key={bi}>{b}</li>
                    ))}
                  </ul>
                )}
                {sec.subSections &&
                  sec.subSections.map((sub, si) => (
                    <div key={si} className="mt-5">
                      <h3 className="font-display text-base sm:text-lg font-bold text-slate-900 mb-2">
                        {sub.heading}
                      </h3>
                      {sub.lead && (
                        <p className="text-[15px] leading-relaxed text-slate-700 mb-3">
                          {sub.lead}
                        </p>
                      )}
                      {sub.bullets && sub.bullets.length > 0 && (
                        <ul className="list-disc pl-5 space-y-2 text-[15px] leading-relaxed text-slate-700">
                          {sub.bullets.map((b, bi) => (
                            <li key={bi}>{b}</li>
                          ))}
                        </ul>
                      )}
                      {sub.body && (
                        <p className="text-[15px] leading-relaxed text-slate-700">
                          {sub.body}
                        </p>
                      )}
                    </div>
                  ))}
                {sec.body && (
                  <p className="text-[15px] leading-relaxed text-slate-700 whitespace-pre-line">
                    {sec.body}
                  </p>
                )}
              </section>
            ))}

            {doc.footer && (
              <p className="mt-10 pt-6 border-t border-slate-200 text-xs text-slate-500 leading-relaxed italic">
                {doc.footer}
              </p>
            )}
          </article>

          <div className="mt-8 text-center text-sm text-[#94A3B8]">
            Questions? Email{" "}
            <a href="mailto:support@genquotes.com" className="text-[#06B6D4] hover:underline">
              support@genquotes.com
            </a>
            . Or read the{" "}
            <Link to={doc.slug === "terms" ? "/privacy" : "/terms"} className="text-[#06B6D4] hover:underline">
              {doc.slug === "terms" ? "Privacy Policy" : "Terms and Conditions"}
            </Link>
            .
          </div>
        </div>
      </section>
    </div>
  );
}

export function Terms() {
  return <LegalDoc doc={TERMS_DOC} />;
}
export function Privacy() {
  return <LegalDoc doc={PRIVACY_DOC} />;
}
